import type {
  BookingConfirmationInsurance,
  BookingConfirmationSummary,
  MyBookingDetail,
} from '@massilia/queries'

/**
 * Maps a MyBookingDetail (from the booking API) to a BookingConfirmationSummary
 * so that existing components like TripRecapSection can be reused without modification.
 */
const toConfirmationInsurance = (insurance: {
  cgsLink: null | string
  dinLink: null | string
  nomAssurance: null | string
  nomFormule: null | string
  price: number
}): BookingConfirmationInsurance => ({
  cgsLink: insurance.cgsLink,
  dinLink: insurance.dinLink,
  nomAssurance: insurance.nomAssurance,
  nomFormule: insurance.nomFormule,
  price: insurance.price,
})

export function mapBookingDetailToConfirmationSummary(
  booking: MyBookingDetail
): BookingConfirmationSummary {
  // Un voyage annulé n'a plus rien à faire dans « votre trajet » : ses vols sont morts.
  const cancelledTripIds = new Set(
    booking.trips.filter((trip) => trip.cancelledAt).map((trip) => trip.id)
  )
  const pnrByTripId = new Map(booking.trips.map((trip) => [trip.id, trip.pnr]))
  const tripConditions = booking.trips
    .filter((trip) => !trip.cancelledAt)
    .flatMap((trip) =>
      trip.exchangeAllowed === null && trip.refundAllowed === null
        ? []
        : [
            {
              conditions: {
                exchangeable: {
                  allowed: trip.exchangeAllowed ?? false,
                  feeDescription: trip.exchangeFeeDescription ?? 'Non modifiable',
                },
                refundable: {
                  allowed: trip.refundAllowed ?? false,
                  feeDescription: trip.refundFeeDescription ?? 'Non remboursable',
                },
              },
              pnr: trip.pnr,
            },
          ]
    )
  // Un contrat par voyage : le cache du dossier ne connaît que celui du premier. Il ne
  // sert que de repli pour un contrat qu'aucun voyage ne porte (dossier antérieur).
  const tripInsuranceIds = new Set(
    booking.trips.flatMap((trip) => (trip.insurance ? [trip.insurance.id] : []))
  )
  const tripInsurances = booking.trips.flatMap((trip) =>
    !trip.cancelledAt && trip.insurance?.activatedAt
      ? [{ insurance: toConfirmationInsurance(trip.insurance), pnr: trip.pnr }]
      : []
  )
  if (
    booking.insurance &&
    booking.insuranceActivatedAt &&
    !tripInsuranceIds.has(booking.insurance.id)
  ) {
    tripInsurances.push({
      insurance: toConfirmationInsurance(booking.insurance),
      pnr: booking.trips[0]?.pnr ?? '',
    })
  }
  return {
    bookingId: booking.id,
    agency: booking.agency,
    bookingReference: booking.reference,
    carRentals: booking.carRentals.map((cr) => ({
      company: cr.company,
      details: cr.details,
      dropOff: cr.dropOff,
      location: cr.location,
      pickUp: cr.pickUp,
      price: cr.price,
    })),
    client: {
      email: booking.client.email,
      phone: booking.client.phoneNumbers?.[0]?.number ?? null,
    },
    // Chaque voyage a SES conditions : celles du dossier ne sont que celles du premier.
    conditions: tripConditions[0]?.conditions,
    currency: booking.currency,
    depositAmountCents: booking.depositAmount ?? undefined,
    depositDeadline: booking.depositDeadline ?? undefined,
    extras: booking.extras.map((e) => ({
      fileName: e.fileName,
      name: e.name,
      providerName: e.providerName,
      description: e.description,
      fileUrl: e.fileUrl,
      price: e.price,
      providerExternalRef: e.providerExternalRef,
    })),
    flightSegments: booking.flightSegments
      .filter((seg) => !cancelledTripIds.has(seg.tripId))
      .map((seg) => ({
        airline: seg.airline,
        arrivalAt: seg.arrivalAt,
        arrivalLocation: seg.arrivalLocation,
        arrivalUtcOffset: seg.arrivalUtcOffset,
        cabinLuggageCount: seg.cabinLuggageCount,
        cabinLuggageWeight: seg.cabinLuggageWeight,
        checkedLuggageCount: seg.checkedLuggageCount,
        checkedLuggageWeight: seg.checkedLuggageWeight,
        departureAt: seg.departureAt,
        departureLocation: seg.departureLocation,
        departureUtcOffset: seg.departureUtcOffset,
        description: seg.description,
        durationMinutes: seg.durationMinutes,
        pnr: pnrByTripId.get(seg.tripId),
        travelClass: seg.travelClass,
      })),
    hotels: booking.hotels.map((h) => ({
      name: h.name,
      checkIn: h.checkIn,
      checkOut: h.checkOut,
      hotelUrl: h.hotelUrl,
      location: h.location,
      pensionType: h.pensionType,
      price: h.price,
      roomType: h.roomType,
    })),
    // Le premier contrat facturé : la section « Assurance » et l'upsell en dépendent
    // encore ; `tripInsurances` porte le détail par voyage.
    insurance: tripInsurances[0]?.insurance ?? null,
    insuranceEstimate: booking.insuranceEstimate,
    passengers: booking.passengers.map((p) => ({
      firstName: p.firstName,
      lastName: p.lastName,
      birthDate: p.birthDate,
      canceled: p.canceled,
      gender: p.gender,
    })),
    status: booking.status,
    totalPriceCents: booking.totalPrice,
    tripConditions,
    tripInsurances,
  }
}
